import { PageHeaderWrapper } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { logError } from "@src/appV2/lib/analytics/log";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { ListLoadingState } from "@src/appV2/redesign/components/ListLoadingState";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useLocation } from "react-router-dom";

import { PageWrapper } from "../../../components/PageWrapper";
import { ClipboardScoreError } from "../ClipboardScoreError";
import { ClipboardScoreNeverNoShowContent } from "./Content";
import { ClipboardScoreNeverNoShowHeader } from "./Header";

export const NEVER_NO_SHOW_SOURCE = {
  CLIPBOARD_SCORE_PAGE: "clipboard_score_page",
  DEEP_LINK: "deep_link",
} as const;

type NeverNoShowSource = (typeof NEVER_NO_SHOW_SOURCE)[keyof typeof NEVER_NO_SHOW_SOURCE];

interface NeverNoShowLocationState {
  source?: NeverNoShowSource;
}

export function ClipboardScoreNeverNoShow() {
  const { userId } = useDefinedWorker();
  const location = useLocation<NeverNoShowLocationState>();

  const source = isDefined(location.state?.source)
    ? location.state.source
    : NEVER_NO_SHOW_SOURCE.DEEP_LINK;

  const {
    data: clipboardScoreProfile,
    isLoading,
    isError,
    error,
    refetch: refetchClipboardScoreProfile,
  } = useGetClipboardScoreProfile(userId);

  const cancelShiftPoints = (clipboardScoreProfile?.policy?.cancelShift ?? []).map(
    (config) => config.points
  );
  const maxCancelShiftPointDeduction =
    cancelShiftPoints.length > 0 ? Math.abs(Math.min(...cancelShiftPoints)) : undefined;

  useLogEffect(
    APP_V2_USER_EVENTS.CLIPBOARD_SCORE_NEVER_NO_SHOW_PAGE_VIEWED,
    {
      workerId: userId,
      pageSource: source,
    },
    { enabled: isDefined(clipboardScoreProfile) && !isLoading && !isError }
  );

  if (isError) {
    logError(APP_V2_APP_EVENTS.CLIPBOARD_SCORE_PROFILE_ERROR, {
      error,
      metadata: { workerId: userId },
    });
    return (
      <ClipboardScoreError
        title="Error loading clipboard score"
        onRefresh={refetchClipboardScoreProfile}
      />
    );
  }

  return (
    <PageWrapper variant="tertiary">
      {isLoading ? (
        <ListLoadingState texts={["Loading clipboard score..."]} />
      ) : (
        <>
          <PageHeaderWrapper variant="tertiary">
            <ClipboardScoreNeverNoShowHeader />
          </PageHeaderWrapper>
          <PageContent sx={{ paddingX: 7 }}>
            <ClipboardScoreNeverNoShowContent
              cancelBeforeShiftStartPointDeduction={maxCancelShiftPointDeduction}
            />
          </PageContent>
        </>
      )}
    </PageWrapper>
  );
}
